const root = '/api/platform';
const tenantPath = id => `${root}/tenants/${encodeURIComponent(id)}`;
const subscription = data => data ? ({ offeringCode: data.offeringCode, allocatedSeats: data.allocatedSeats,
  status: data.status, startsAt: data.startsAt, endsAt: data.endsAt, entitlements: data.entitlements }) : null;
const tenant = data => data ? ({ id: data.id, name: data.name, domain: data.domain, status: data.status,
  authenticationMode: data.authenticationMode, subscription: subscription(data.subscription), ssoConfigured: data.ssoConfigured }) : null;
const createdTenant = data => data ? ({ ...tenant(data), invitationToken: data.invitationToken, invitationExpiresAt: data.invitationExpiresAt }) : null;

// Explicit DTO projections: never retain/render hypothetical persistence fields or secrets.
export function createPlatformApi(http) {
  return {
    tenants: () => http.get(`${root}/tenants`).then(r => (r.data || []).map(tenant)),
    tenant: id => http.get(tenantPath(id)).then(r => tenant(r.data)),
    createTenant: ({ companyName, domain, adminEmail, adminName, offeringCode, allocatedSeats }) =>
      http.post(`${root}/tenants`, { companyName, domain, adminEmail, adminName, offeringCode, allocatedSeats }).then(r => createdTenant(r.data)),
    offerings: () => http.get(`${root}/offerings`).then(r => (r.data || []).map(o => ({ code: o.code, displayName: o.displayName,
      deploymentModel: o.deploymentModel, mcpIncluded: o.mcpIncluded, trial: o.trial, entitlements: o.entitlements }))),
    subscription: id => http.get(`${tenantPath(id)}/subscription`).then(r => subscription(r.data)),
    changeSubscription: (id, { offeringCode, allocatedSeats, status, startsAt, endsAt }) =>
      http.put(`${tenantPath(id)}/subscription`, { offeringCode, allocatedSeats, status, startsAt, endsAt }).then(r => subscription(r.data)),
    status: (id, status) => http.patch(`${tenantPath(id)}/status`, { status }).then(r => tenant(r.data)),
    mode: (id, mode) => http.put(`${tenantPath(id)}/authentication-mode`, { mode }).then(r => tenant(r.data)),
    sso: id => http.get(`${tenantPath(id)}/sso-status`).then(({ data }) => data ? ({ configured: data.configured,
      enabled: data.enabled, lastTestedAt: data.lastTestedAt, issuer: data.issuer, scopes: data.scopes }) : null),
    transfer: (id, tenantUserId) => http.put(`${tenantPath(id)}/tenant-admin`, { tenantUserId }).then(() => undefined),
  };
}

const knownMessages = new Set([
  'Tenant not found', 'Active subscription not found', 'Offering not found',
  'Allocated seats cannot be below active users', 'TRIAL requires exactly 10 seats',
  'Allocated seats must be one of 3, 5, 10, 25, 50, 100',
  'SSO configuration must be tested before activation', 'Authentication mode must be LOCAL or SSO',
  'Current administrator is missing', 'Replacement user is invalid', 'Cobygo Admin role required',
  'A tenant already exists for this domain',
]);
export function platformError(error) {
  const response = error?.response;
  const readinessErrors = {
    SSO_NOT_CONFIGURED: 'Configure the tenant’s default OIDC provider first.',
    SSO_PROVIDER_INVALID: 'The default provider must use OIDC.',
    SSO_PROVIDER_DISABLED: 'The default OIDC provider must be enabled.',
    SSO_PROVIDER_NOT_TESTED: 'Test the saved OIDC provider before enabling SSO.',
    SSO_ADMIN_COUNT_INVALID: 'Exactly one active Tenant Admin is required before enabling SSO.',
    SSO_ADMIN_IDENTITY_REQUIRED: 'The active Tenant Admin must enable SSO for their user from Tenant Admin → Single sign-on before you activate SSO.',
  };
  const code = response?.data?.code === 'AUTH_REQUEST_ERROR' ? String(response.data.message || '').split(':')[0] : response?.data?.code;
  if (readinessErrors[code]) return readinessErrors[code];
  if (knownMessages.has(response?.data?.message)) return response.data.message;
  if (response?.status === 400) return 'Check the submitted fields. The server rejected the request.';
  if (response?.status === 401) return 'Your platform session has ended. Sign in again.';
  if (response?.status === 403) return 'Cobygo Platform Admin permission is required.';
  if (response?.status === 404) return 'The requested platform record was not found.';
  if (response?.status === 409) return 'The requested change conflicts with the current tenant state. Reload and review it.';
  if (response?.status === 429) return 'Too many requests. Wait before trying again.';
  return 'Unable to complete the platform request. Check your connection and try again.';
}

// Contract from SubscriptionService.validateSeats, not pricing or seat-specific plans.
export const seatPackages = offering => offering?.trial ? [10] : [3, 5, 10, 25, 50, 100];
export const SUBSCRIPTION_STATES = ['ACTIVE', 'EXPIRED', 'CANCELLED', 'SUSPENDED'];
